import { Media } from '@synergy/models';
import { useCallback, useSyncExternalStore } from 'react';

/*
 * The files of a position as they are since the mask was opened.
 *
 * A position's files are saved the moment they are added, through their own
 * endpoint, so they are no input of the form: written into its row, they made
 * the document read as unsaved (R-198), and the attachments rail, which reads
 * the loaded document, showed them only after a reload (R-195). Kept here per
 * position, the dialog and the rail read the same thing, and the form stays
 * as it was.
 */
const changed = new Map<number, Media[]>();
const listeners = new Set<() => void>();
let version = 0;

const subscribe = (listener: () => void) => {
    listeners.add(listener);

    return () => listeners.delete(listener);
};

export const setPaloPositionMedia = (positionId: number, media: Media[]): void => {
    changed.set(positionId, media);
    version++;
    listeners.forEach(listener => listener());
};

/** The files of a position: those changed since opening, or the ones it was loaded with. */
export const paloPositionMedia = (positionId: number | undefined, loaded?: Media[]): Media[] =>
    (undefined !== positionId ? changed.get(positionId) : undefined) ?? loaded ?? [];

/** The same, for a component that has to redraw when a position's files change. */
export const usePaloPositionMedia = (): ((positionId: number | undefined, loaded?: Media[]) => Media[]) => {
    const current = useSyncExternalStore(subscribe, () => version);

    return useCallback(
        (positionId: number | undefined, loaded?: Media[]) => paloPositionMedia(positionId, loaded),
        // `current` makes a new lookup whenever a position's files change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [current]
    );
};
